package com.iregained.site.components.sections.contact.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.contact.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.region.activeStrings
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.ObfuscatedEmailText
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

// UNIQUE ANIMATION: Staggered Glide-Up
@Composable
private fun Modifier.staggeredGlideUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ContactSupportSection(data: ContactSupportSectionData = defaultContactSupportData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "contact-support-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.supportCategories.isNotEmpty()) {
        Column(
            modifier = ContactSupportContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionTitle,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().color(palette.custom.darkText)
                                .fontWeight(FontWeight.Medium).textAlign(TextAlign.Center).maxWidth(700.px)
                                .margin(bottom = 60.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                    }
                                })
                    }

                    SimpleGrid(numColumns(1, lg = 3), modifier = ContactSupportGridStyle.toModifier()) {
                        data.supportCategories.forEachIndexed { index, category ->
                            key(category.title) {
                                Box(
                                    modifier = SupportCardStyle.toModifier()
                                        .staggeredGlideUp(isVisible, 300 + (index * 150))
                                ) {
                                    Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.Start) {
                                        if (category.iconPath.isNotBlank()) Box(
                                            modifier = SupportIconWrapperStyle.toModifier(),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Image(
                                                src = category.iconPath,
                                                alt = category.title,
                                                modifier = SupportIconStyle.toModifier()
                                            )
                                        }
                                        if (category.title.isNotBlank()) SpanText(
                                            text = category.title, modifier = SupportCardTitleStyle.toModifier()
                                        )
                                        if (category.description.isNotBlank()) Div(
                                            attrs = SupportCardDescStyle.toModifier().toAttrs {
                                                ref { htmlElement ->
                                                    htmlElement.innerHTML = category.description; onDispose {}
                                                }
                                            })
                                        if (category.links.isNotEmpty()) {
                                            Column(modifier = SupportLinkContainerStyle.toModifier()) {
                                                category.links.forEach { linkData ->
                                                    key(linkData.displayText) {
                                                        if (linkData.url.isNotBlank() && linkData.displayText.isNotBlank()) {
                                                            // INTERCEPT SENSITIVE LINKS
                                                            if (linkData.url.startsWith("mailto:") || linkData.url.startsWith("tel:")) {
                                                                A(
                                                                    attrs = SupportLinkItemStyle.toModifier()
                                                                        .cursor(Cursor.Pointer)
                                                                        .onClick { e ->
                                                                            e.preventDefault()
                                                                            kotlinx.browser.window.location.assign(linkData.url)
                                                                        }
                                                                        .toAttrs { attr("role", "link"); attr("tabindex", "0") }
                                                                ) {
                                                                    // APPLY OBFUSCATION ONLY TO EMAILS VISUALLY:
                                                                    if (linkData.url.startsWith("mailto:")) {
                                                                        ObfuscatedEmailText(email = linkData.displayText)
                                                                    } else {
                                                                        Text(linkData.displayText) // Leave phone numbers visually intact
                                                                    }
                                                                }
                                                            } else {
                                                                // STANDARD WEB LINKS RENDER NORMALLY
                                                                Link(
                                                                    path = linkData.url,
                                                                    text = linkData.displayText,
                                                                    modifier = SupportLinkItemStyle.toModifier()
                                                                )
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR SUPPORT ICONS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px.
 * - Format: SVG (Monochromatic/Teal).
 * - Content: Icons representing Hardware (wrench/gear), Software (laptop/cloud), and HQ (map pin/building).
 */
data class SupportLinkData(
    val displayText: String, val url: String // Can be "mailto:...", "tel:...", or standard URLs
)

data class SupportCategoryData(
    val iconPath: String, val title: String, val description: String, val links: List<SupportLinkData>
)

data class ContactSupportSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Global Support & HQ",
    val sectionSubtitle: String = "Whether you are a patient, a clinical administrator, or an investor, our team is directly accessible.",
    val supportCategories: List<SupportCategoryData> = listOf(
        // CARD 1: HARDWARE & TECHNICAL (Uses general inquire email)
        SupportCategoryData(
            iconPath = Res.Images.About.Values.HARDWARE,
            title = "Hardware & Technical",
            description = "For immediate assistance with device setup, maintenance, mechatronic calibration, or replacement parts.",
            links = listOf(
                SupportLinkData(activeStrings.supportEmail, "mailto:${activeStrings.supportEmail}"), SupportLinkData(
                    activeStrings.supportPhone, "tel:${activeStrings.supportPhone.replace(Regex("[^0-9+]"), "")}"
                )
            )
        ),

        // CARD 2: CLINICAL DTX SOFTWARE (Uses dedicated support email)
        SupportCategoryData(
            iconPath = Res.Images.About.Values.SOFTWARE,
            title = "Clinical DTx Software",
            description = "For questions regarding patient data export, EMR integration, gameplay protocols, and clinical trial results.",
            links = listOf(
                SupportLinkData(activeStrings.supportEmail, "mailto:${activeStrings.supportEmail}"), SupportLinkData(
                    activeStrings.supportPhone, "tel:${activeStrings.supportPhone.replace(Regex("[^0-9+]"), "")}"
                )
            )
        ),

        // CARD 3: GLOBAL HEADQUARTERS (Uses dedicated investor email)
        SupportCategoryData(
            iconPath = Res.Images.Contact.Support.HEADQUATER,
            title = "Global Headquarters",
            description = "Our engineering lab, manufacturing hub, and corporate offices are proudly located in Northern Ontario.",
            links = listOf(
                SupportLinkData("Greater Sudbury, ON, Canada", "https://share.google/7i3EqMgPV13itIzeE"),
                SupportLinkData(activeStrings.investorEmail, "mailto:${activeStrings.investorEmail}")
            )
        ),
    )
)

val defaultContactSupportData = ContactSupportSectionData()